import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Box, Chip, CircularProgress, Typography, Button, Grid, Modal } from "@mui/material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DetailsCard from "../../../../../customComponents/DetailsCard";
import MathContent from "../../../../customComponents/MathContent";
import { FilledButton } from "../../../../../customComponents/styled/styledButtons";
import { useSubmitQuizMutation, useStartQuizMutation, useGetQuizByIdQuery } from "../../../../../app/quizApiSlice";
import recentShared from "../../../../../assets/icons/recent-shared.svg";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import { useNetworkStatus } from "../../../../../hooks/useNetworkStatus";
import db from "../../../../../app/offlineDb";

const seededShuffle = (array, seed) => {
  const shuffled = [...array];
  let m = shuffled.length;
  let s = seed;
  const rng = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  while (m) {
    const i = Math.floor(rng() * m--);
    [shuffled[m], shuffled[i]] = [shuffled[i], shuffled[m]];
  }
  return shuffled;
};

const PracticeQuiz = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const { quiz: quizFromState, returnPath } = location.state || {};
  const backPath = returnPath || "/lms/student/class-quiz";

  // When location.state is lost (page refresh), re-fetch the quiz by ID from the URL
  const { data: fetchedQuizData, isLoading: quizFetching } = useGetQuizByIdQuery(
    routeId,
    { skip: !!quizFromState }
  );
  const effectiveQuiz = quizFromState || fetchedQuizData?.quiz;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [submitQuiz, { isLoading }] = useSubmitQuizMutation();
  const [startQuiz] = useStartQuizMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTimeUpOpen, setIsTimeUpOpen] = useState(false);
  const [isInstructionExpanded, setIsInstructionExpanded] = useState(false);
  // Initialise from location.state (if available); the server-anchor timer corrects it otherwise
  const [timeLeft, setTimeLeft] = useState(quizFromState?.duration ? quizFromState.duration * 60 : 0);
  const { isOnline } = useNetworkStatus();
  const timerInitialisedRef = useRef(false);

  // Derive a stable session key from quizId + student (keyed off localStorage user)
  const userId = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user"))?.id ?? 0; } catch { return 0; }
  }, []);

  // Deterministically shuffle questions per student & quiz so different users get different question orders,
  // while remaining consistent across browser refreshes for the same user.
  const questions = React.useMemo(() => {
    if (!effectiveQuiz?.questions?.length) return [];
    const seed = (effectiveQuiz.id || 1) * 100003 + (userId || 1);
    return seededShuffle(effectiveQuiz.questions, seed);
  }, [effectiveQuiz?.questions, effectiveQuiz?.id, userId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      quiz_id: effectiveQuiz?.id || 0,
      answers: questions.map((question) => ({
        question_id: question.id,
        option_id: 0,
      })),
    },
    onSubmit: async (values) => {
      try {
        const result = await submitQuiz(values).unwrap();
        // Clear the local session cache (regardless of online/offline queued)
        if (effectiveQuiz?.id) {
          db.quizSessions.delete([effectiveQuiz.id, userId]).catch(() => {});
        }
        if (result?.queued) {
          // Submitted while offline — queued for sync
          alert("Your answers have been saved and will be submitted when you reconnect.");
        }
        // Navigate back regardless of retakes_remaining — the ClassQuiz list
        // will re-render with the Retake button via cache invalidation.
        navigate(backPath);
      } catch (error) {
        console.error("Failed to submit quiz:", error);
      } finally {
        setIsModalOpen(false);
      }
    },
  });

  // Restore saved answers from IndexedDB on mount (handles mid-quiz disconnections / refreshes)
  useEffect(() => {
    if (!effectiveQuiz?.id) return;
    db.quizSessions
      .get([effectiveQuiz.id, userId])
      .then((session) => {
        if (session?.answers?.length) {
          formik.setFieldValue("answers", session.answers);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveQuiz?.id, userId]);

  // ── Server-anchored timer ───────────────────────────────────────────────────
  // Call /quiz/<id>/start on mount to get (or create) a server-recorded
  // started_at timestamp. The remaining time is recomputed from that anchor,
  // so a page refresh cannot reset the countdown.
  useEffect(() => {
    if (!effectiveQuiz?.id || !effectiveQuiz?.duration || timerInitialisedRef.current) return;
    timerInitialisedRef.current = true;

    startQuiz(effectiveQuiz.id)
      .unwrap()
      .then(({ started_at, server_time }) => {
        const startMs = new Date(started_at).getTime();
        const nowMs = new Date(server_time).getTime();
        const elapsedSeconds = Math.floor((nowMs - startMs) / 1000);
        const remaining = Math.max(0, effectiveQuiz.duration * 60 - elapsedSeconds);
        setTimeLeft(remaining);
        // Also persist startedAt locally for offline fallback
        db.quizSessions
          .get([effectiveQuiz.id, userId])
          .then((existing) => {
            const record = existing || { quizId: effectiveQuiz.id, studentId: userId, answers: formik.values.answers, status: "in-progress", updatedAt: Date.now() };
            db.quizSessions.put({ ...record, startedAt: startMs }).catch(() => {});
          })
          .catch(() => {});
      })
      .catch(() => {
        // Offline: try to recover startedAt from IndexedDB
        db.quizSessions
          .get([effectiveQuiz.id, userId])
          .then((session) => {
            if (session?.startedAt) {
              const elapsedSeconds = Math.floor((Date.now() - session.startedAt) / 1000);
              const remaining = Math.max(0, effectiveQuiz.duration * 60 - elapsedSeconds);
              setTimeLeft(remaining);
            }
            // else leave default full duration — server will reject if truly expired
          })
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveQuiz?.id, effectiveQuiz?.duration]);

  useEffect(() => {
    if (!effectiveQuiz) return;
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [effectiveQuiz]);

  useEffect(() => {
    if (timeLeft <= 0 && effectiveQuiz?.duration) {
      formik.submitForm();
      setIsTimeUpOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleAnswer = (optionId) => {
    if (!effectiveQuiz) return;
    const newAnswers = formik.values.answers.map((answer, index) =>
      index === currentQuestionIndex ? { ...answer, option_id: optionId } : answer
    );
    formik.setFieldValue("answers", newAnswers);
    // Persist progress to IndexedDB so answers survive a page refresh
    db.quizSessions
      .get([effectiveQuiz.id, userId])
      .then((existing) => {
        const record = existing || { quizId: effectiveQuiz.id, studentId: userId, status: "in-progress" };
        db.quizSessions.put({ ...record, answers: newAnswers, updatedAt: Date.now() }).catch(() => {});
      })
      .catch(() => {});
  };

  const handleNext = () => {
    if (!effectiveQuiz) return;
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setIsModalOpen(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Arrow key navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handlePrevious();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (!effectiveQuiz) {
    if (quizFetching) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Quiz not found</Typography>
        <Button onClick={() => navigate(backPath)} sx={{ mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  const question = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const instructionText = effectiveQuiz.instruction?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || "";
  const hasInstruction = Boolean(instructionText);
  const isLongInstruction = instructionText.length > 100;

  return (
    <Box>
      {!isOnline && (
        <Chip
          icon={<WifiOffIcon sx={{ fontSize: 16 }} />}
          label="Offline — answers are saved locally and will sync when you reconnect"
          color="warning"
          variant="filled"
          sx={{ mb: 2, fontWeight: 600 }}
        />
      )}
      <DetailsCard
        header={`${effectiveQuiz.subject} ${effectiveQuiz.type}`}
        subHeading1={"Date"}
        value1={formatDate(effectiveQuiz.updated_at)}
        subHeading2={"Subject"}
        value2={effectiveQuiz.subject}
        subHeading3={"Level"}
        value3={effectiveQuiz.level}
      />

      {hasInstruction && (
        <Box
          sx={{
            mb: 3,
            p: { xs: 2, md: 2.5 },
            borderRadius: "10px",
            border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#E0E0E0"}`,
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#FFFFFF",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Instructions
            </Typography>
            {isLongInstruction && (
              <Button
                size="small"
                endIcon={isInstructionExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                onClick={() => setIsInstructionExpanded((current) => !current)}
                sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
              >
                {isInstructionExpanded ? "Show less" : "Show more"}
              </Button>
            )}
          </Box>
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.7,
              display: "-webkit-box",
              WebkitLineClamp: isLongInstruction && !isInstructionExpanded ? 2 : "unset",
              WebkitBoxOrient: "vertical",
              overflow: isLongInstruction && !isInstructionExpanded ? "hidden" : "visible",
              "& p": { m: 0 },
              "& p + p": { mt: 1 },
              "& ul, & ol": { my: 0, pl: 3 },
            }}
          >
            <MathContent html={effectiveQuiz.instruction} />
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "2em",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", lineHeight: "1.75em", fontWeight: 700 }}
        >
          Question {currentQuestionIndex + 1}/{questions.length}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#FCC21B",
            lineHeight: "1.5em",
            fontSize: "1rem",
            fontWeight: 700,
          }}
        >
          {formatTime(timeLeft)}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: "3em",
          marginBottom: "3em",
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#3a2e00" : "#FEECBA",
          textAlign: "center",
          border: "0.5px solid #FCC21B",
          borderRadius: "6px",
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: "1.1rem", fontWeight: 700, lineHeight: "2em" }}
        >
          <MathContent html={question?.body} />
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {question?.options?.map((option) => (
          <Grid item xs={6} key={option.id} sx={{ textAlign: "left" }}>
            <Button
              fullWidth
              variant={
                formik.values.answers[currentQuestionIndex]?.option_id ===
                option.id
                  ? "contained"
                  : "outlined"
              }
              onClick={() => handleAnswer(option.id)}
              sx={{
                backgroundColor:
                  formik.values.answers[currentQuestionIndex]?.option_id ===
                  option.id
                    ? (theme) => theme.palette.mode === "dark" ? "#3a2e00" : "#FEECBA"
                    : (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                color: "text.primary",
                fontSize: "1rem",
                fontWeight: 700,
                border:
                  formik.values.answers[currentQuestionIndex]?.option_id ===
                  option.id
                    ? "0.89px solid #FCC21B"
                    : "0.89px solid #DAD9D7",
                padding: "1em",
                textAlign: "left",
                boxShadow: "none",
                borderRadius: "14px",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "#4a3d00" : "#FEECBA",
                },
              }}
            >
              <MathContent html={option.body} />
            </Button>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            width: "40%",
          }}
        >
          <FilledButton
            disabled={currentQuestionIndex === 0}
            onClick={handlePrevious}
            sx={{
              color: "#FCC21B",
              border: "1px solid #FCC21B",
              padding: "1em",
              fontSize: "18px",
              fontWeight: "700",
              lineHeight: "24px",
              textAlign: "center",
              whiteSpace: "nowrap",
              minWidth: "10px",
              height: "48px",
              textTransform: "none",
              backgroundColor: "transparent",
              borderRadius: "10px",
              width: "10em",
              "&:hover": {
                backgroundColor: "#FCC21B",
                color: "#F9F9F9",
              },
            }}
          >
            Previous
          </FilledButton>
          <FilledButton
            onClick={handleNext}
            sx={{
              padding: "1em 3em",
              fontSize: "1rem",
              fontWeight: 700,
              borderRadius: "10px",
              width: "10em",
            }}
          >
            {isLastQuestion ? "Submit" : "Next"}
          </FilledButton>
        </Box>
      </Box>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            bgcolor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#FFFFFF",
            p: 4,
            borderRadius: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
              padding: "1em",
              textTransform: "capitalize",
              width: "100%",
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              borderRadius: "21px",
            }}
          >
            <Box sx={{ width: "2.7rem" }}>
              <img src={recentShared} alt="" style={{ width: "100%" }} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                lineHeight: "1.75rem",
              }}
            >
              {effectiveQuiz.subject} {effectiveQuiz.type}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: ".9rem",
                lineHeight: "1.75rem",
                fontWeight: 400,
              }}
            >
              {formatDate(effectiveQuiz.updated_at)}
            </Typography>
          </Box>
          <Box sx={{ margin: "1em 0", textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, fontSize: "1.25rem" }}
            >
              Confirmation
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: ".9rem",
                lineHeight: "1.75rem",
                fontWeight: 400,
              }}
            >
              Are you sure you want to submit this quiz?
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "60%",
            }}
          >
            <FilledButton
              onClick={() => setIsModalOpen(false)}
              sx={{ width: "10em" }}
            >
              Cancel
            </FilledButton>
            <FilledButton
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
              sx={{ width: "10em" }}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </FilledButton>
          </Box>
        </Box>
      </Modal>

      {/* Time's Up modal */}
      <Modal open={isTimeUpOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "85%", sm: "420px" },
            bgcolor: "background.paper",
            borderRadius: "24px",
            p: 4,
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            outline: "none",
          }}
        >
          {/* Icon badge */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: (t) => t.palette.mode === "dark" ? "#3a2e00" : "#FEF3C7",
              border: "2px solid #FCC21B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <AccessAlarmIcon sx={{ fontSize: 38, color: "#FCC21B" }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Time&rsquo;s Up!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            Your time has run out. Your answers have been automatically submitted.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setIsTimeUpOpen(false);
              navigate(backPath);
            }}
            sx={{
              borderRadius: "12px",
              py: 1.25,
              fontWeight: 700,
              fontSize: "1rem",
              bgcolor: "#FCC21B",
              color: "#1a1a1a",
              boxShadow: "none",
              "&:hover": { bgcolor: "#f0b800", boxShadow: "none" },
            }}
          >
            {isLoading ? <CircularProgress size={22} sx={{ color: "#1a1a1a" }} /> : "Continue"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default PracticeQuiz;
